import React, { useEffect, useRef, useState } from 'react';
import ZoomOutIcon from '../../assets/icons/minus.svg';
import ZoomInIcon from '../../assets/icons/plus.svg';
import MinimizeIcon from '../../assets/icons/zoom-fit.svg';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { fetchAllDrivers, fetchAllOrgChartUsers } from '../../services/orgChartApiService';
import { Card } from '../ui';
import ProfileImage from '../ui/ProfileImage';
import OrgChartNode from './OrgChartNode';

const TEXT_COLOR = 'rgba(0,0,0,0.87)';
const MIN_ZOOM = 0.2;
const MAX_ZOOM = 2;

const getRoleColor = (roleName) => {
  if (!roleName) return 'gray';
  switch (roleName.toLowerCase()) {
    case 'admin': return 'blue';
    case 'supervisor': return 'green';
    case 'guard': return 'orange';
    case 'driver': return 'purple';
    default: return 'gray';
  }
};

const OrgChartHierarchy = () => {
  // Log screen view
  useScreenLogging('OrgChartHierarchy', {
    component: 'OrgChartHierarchy',
  });
  const [data, setData] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [scrollStart, setScrollStart] = useState({ x: 0, y: 0 });
  const scrollContainerRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    async function loadData() {
      setIsLoading(true);
      try {
        const users = await fetchAllOrgChartUsers();
        let drivers = [];
        try {
          drivers = await fetchAllDrivers();
        } catch {}
        const allUsers = [...users, ...drivers];
        setData(allUsers);
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    }
    loadData();
  }, []);

  const transformData = () => {
    return data.map(item => {
      const employeeId = item.employee_id?.toString() || '';
      let parentId = item.reports_to?.length ? item.reports_to[0]?.toString() : null;
      if (parentId === employeeId) parentId = null;
      if (parentId && !data.some(d => d.employee_id?.toString() === parentId)) parentId = null;

      const rawRole = item.spec_role?.toString().trim();
      const normalizedRole = rawRole && rawRole.toLowerCase() !== 'unknown' ? rawRole : 'Employee';

      // Determine user_id for profile image fetching - try multiple fields like DeleteEmployeeProfile does
      // Priority: user_id > employee_id > id (for drivers and other users)
      const userIdForImage = item.user_id || item.employee_id || item.id;

      return {
        id: employeeId,
        parent: parentId,
        name: item.user_name || 'Unknown',
        role: normalizedRole,
        roleName: item.role_name || '',
        color: getRoleColor(item.role_name),
        image_path: item.image_path || '', // Pass image_path if available, ProfileImage will handle it
        user_id: userIdForImage, // Store user_id for ProfileImage to fetch if needed (with fallback)
      };
    });
  };

  const nodesArray = transformData();
  const nodesById = {};
  nodesArray.forEach(node => {
    nodesById[node.id] = { ...node, children: [] };
  });
  let rootNodes = [];
  nodesArray.forEach(node => {
    if (node.parent && nodesById[node.parent]) {
      nodesById[node.parent].children.push(nodesById[node.id]);
    } else {
      rootNodes.push(nodesById[node.id]);
    }
  });

  const handleSearch = (query) => {
    const trimmedQuery = query.trim().toLowerCase();
    setSearchTerm(query);
    if (!trimmedQuery) {
      setShowResults(false);
      setSearchResults([]);
      return;
    }
    const results = nodesArray.filter(node =>
      node.name.toLowerCase().includes(trimmedQuery)
    );
    setSearchResults(results);
    setShowResults(true);
  };

  const onResultSelected = async (node) => {
    setSelectedId(node.id);
    setShowResults(false);
    setSearchTerm(node.name);

    setTimeout(() => {
      const nodeElement = document.getElementById(`org-node-${node.id}`);
      if (nodeElement && scrollContainerRef.current) {
        nodeElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }
    }, 100);
  };

  const renderTreeNode = (node) => (
    <div key={node.id} id={`org-node-${node.id}`} className="flex flex-col items-center">
      <OrgChartNode
        node={node}
        isSelected={selectedId === node.id}
        onClick={() => setSelectedId(node.id)}
      />
      {node.children.length > 0 && (
        <>
          <div className="w-1 h-20 bg-black" />
          <div className="relative flex justify-center items-center">
            <div className="absolute left-0 right-0 top-0 z-0 h-1 bg-black pointer-events-none" />
            <div className="z-10 flex space-x-10">
              {node.children.map(child => (
                <div key={child.id} className="flex flex-col items-center">
                  <div className="w-1 h-8 bg-black" />
                  {renderTreeNode(child)}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );

  const zoomOut = () => setZoomLevel((z) => Math.max(z - 0.2, MIN_ZOOM));
  const zoomIn = () => setZoomLevel((z) => Math.min(z + 0.2, MAX_ZOOM));

  const handleMouseDown = (e) => {
    // Don't drag if clicking on interactive elements (nodes, buttons, inputs, etc.)
    if (
      e.target.closest('[data-node]') || 
      e.target.closest('[data-node-id]') ||
      e.target.closest('button') || 
      e.target.closest('input')
    ) {
      return;
    }
    
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    if (scrollContainerRef.current) {
      setScrollStart({
        x: scrollContainerRef.current.scrollLeft,
        y: scrollContainerRef.current.scrollTop,
      });
    }
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging && scrollContainerRef.current) {
        const deltaX = dragStart.x - e.clientX;
        const deltaY = dragStart.y - e.clientY;
        
        scrollContainerRef.current.scrollLeft = scrollStart.x + deltaX;
        scrollContainerRef.current.scrollTop = scrollStart.y + deltaY;
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, dragStart, scrollStart]);

  // Helper: center viewport on the chart element using its actual bounds
  const centerOnChart = (behavior = 'auto') => {
    const container = scrollContainerRef.current;
    const chart = chartRef.current;
    if (!container || !chart) return;
    // Allow layout/scale to settle
    setTimeout(() => {
      const chartCenterX = chart.offsetLeft + chart.offsetWidth / 2;
      const chartCenterY = chart.offsetTop + chart.offsetHeight / 2;
      const targetLeft = chartCenterX - container.clientWidth / 2;
      const targetTop = chartCenterY - container.clientHeight / 2;
      container.scrollTo({
        left: Math.max(0, targetLeft),
        top: Math.max(0, targetTop),
        behavior,
      });
    }, 50);
  };

  const handleMinimize = () => {
    setZoomLevel(MIN_ZOOM);
    // Use requestAnimationFrame to ensure DOM has updated after zoom change
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        centerOnChart('smooth');
      });
    });
  };
  
  // Center the chart in view after initial load
  useEffect(() => {
    if (!isLoading && scrollContainerRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          centerOnChart('auto');
        });
      });
    }
  }, [isLoading]);

  return (
    <div className="flex flex-col w-full h-full min-h-0" style={{ color: TEXT_COLOR }}>
      {/* Toolbar */}
      <div className="sticky top-0 z-50 w-full">
        <div className="w-full flex items-center justify-between">
          
          {/* --- Glassmorphic search bar with dropdown --- */}
          <div className="relative w-96 h-12 pl-4 pr-3.5 py-2.5 bg-neutral-50/80 rounded-[10px] shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.1px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-white/10 backdrop-blur-[30px] flex items-center gap-4">
            {/* Icon */}
            <div className="w-6 h-6 flex items-center justify-center">
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8" stroke="#C5C7CD" strokeWidth="1.5" />
                <line x1="17" y1="17" x2="22" y2="22" stroke="#C5C7CD" strokeWidth="1.5" />
              </svg>
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={e => handleSearch(e.target.value)}
              placeholder="Search by Employee"
              className="flex-1 bg-transparent border-none outline-none text-lg font-bold text-neutral-500 placeholder-neutral-400 font-['Figtree']"
              style={{ boxShadow: 'none' }}
            />
            {/* Dropdown */}
            {showResults && searchResults.length > 0 && (
              <div
                className="absolute left-0 top-full mt-2 w-full bg-white border border-gray-200 rounded shadow-md max-h-60 overflow-auto z-20"
              >
                {searchResults.map(node => (
                  <div
                    key={node.id}
                    className="cursor-pointer px-4 py-2 hover:bg-gray-100 flex items-center"
                    onClick={() => onResultSelected(node)}
                  >
                    <ProfileImage
                      userId={node.user_id}
                      userName={node.name}
                      imagePath={node.image_path && !node.image_path.startsWith('s3://') ? node.image_path : null}
                      size="sm"
                      showLoading={false}
                      className="w-8 h-8 rounded-full mr-2"
                    />
                    <div>
                      <div className="font-medium">{node.name}</div>
                      <div className="text-sm text-gray-500">{node.role}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2">
            <div className="w-20 h-12 relative bg-neutral-50/40 rounded-xl outline outline-1 outline-offset-[-1px] outline-white/0 overflow-hidden flex-shrink-0">
              <button
                className="w-8 h-8 left-[8px] top-[8px] absolute bg-neutral-50/10 rounded-lg outline outline-[0.67px] outline-offset-[-0.67px] outline-white/0 overflow-hidden flex items-center justify-center"
                onClick={zoomOut}
                title="Zoom Out"
              >
                <img src={ZoomOutIcon} alt="Zoom Out" className="w-2 h-3" />
              </button>
              <button
                className="w-8 h-8 left-[48px] top-[8px] absolute bg-neutral-50/10 rounded-lg outline outline-[0.67px] outline-offset-[-0.67px] outline-white/0 overflow-hidden flex items-center justify-center"
                onClick={zoomIn}
                title="Zoom In"
              >
                <img src={ZoomInIcon} alt="Zoom In" className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={handleMinimize}
              title="Minimize"
              className="w-8 h-8 bg-neutral-50/10 rounded-lg outline outline-[0.67px] outline-offset-[-0.67px] outline-white/0 overflow-hidden flex items-center justify-center ml-2"
            >
              <img src={MinimizeIcon} alt="Minimize" className="w-5 h-5 mx-auto" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Card Area */}
      <Card.Gradient className="rounded-[20px] shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-white/30 flex-1 mt-2 p-1 relative overflow-hidden">
        <div
          ref={scrollContainerRef}
          className={`w-full h-full overflow-auto scroll-container ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          style={{ position: 'relative', minHeight: 0 }}
          onMouseDown={handleMouseDown}
        >
          <div className="min-w-full min-h-full w-max h-max flex items-center justify-center p-10">
            <div
              ref={chartRef}
              style={{
                transform: `scale(${zoomLevel})`,
                transformOrigin: 'center center',
                display: 'inline-block'
              }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="w-8 h-8 border-4 rounded-full animate-spin border-t-transparent" />
                </div>
              ) : rootNodes.length === 0 ? (
                <div className="text-center mt-10">No organizational data available</div>
              ) : (
                <div className="flex justify-center">
                  {rootNodes.map(node => renderTreeNode(node))}
                </div>
              )}
            </div>
          </div>
        </div>
      </Card.Gradient>
    </div>
  );
};

export default OrgChartHierarchy;
